import "./styleSms.css";
import { useRef, ChangeEvent, useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from "../../../../services/api";
import AlertMessage from '../../../Alert';
import { CadastroContext } from "../../../../contexts/CadastroContext";

const AsoValidaTokenSms = () => {
   const [alert, setAlert] = useState<{ show: boolean; message: string; variant: string }>({ show: false, message: "", variant: "success" });
   const { cadastro, handleCadastroChange } = useContext(CadastroContext);
   const countdownTime = 60;
   const [time, setTime] = useState(countdownTime);
   const [timerEnded, setTimerEnded] = useState(false);
   const [values, setValues] = useState<string[]>(["", "", "", ""]);
   const inputRefs:any = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
   const memoizedValues = useMemo(() => values, [values]);
   const { telefoneInput, dbName } = useParams();
   const location = useLocation();

   useEffect(() => {
      const timerInterval = setInterval(() => {
         if (time <= 0) {
            clearInterval(timerInterval);
            setTimerEnded(true);
         } else {
            setTime(prevTime => prevTime - 1);
         }
      }, 1000);

      return () => clearInterval(timerInterval);
   }, [time]);

   const handleResendCode = () => {
      setValues(["", "", "", ""]);
   };

   const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>, index: number) => {
      const { value } = e.target as HTMLInputElement;
      const newValues = [...values];

      if (/^\d*$/.test(value)) {  // Ensure only numeric input
         newValues[index] = value;
         setValues(newValues);

         if (value.length === 1 && index < inputRefs.length - 1) {
            inputRefs[index + 1]?.current?.focus();
         }
      }

      if (e.type === 'keydown') {
         const keyboardEvent = e as React.KeyboardEvent<HTMLInputElement>;
         if (keyboardEvent.key === 'Backspace' && index > 0 && value.length === 0) {
            inputRefs[index - 1]?.current?.focus();
         }
      }
   }, [values, inputRefs]);

   const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedData = e.clipboardData.getData('text');
      if (/^\d+$/.test(pastedData) && pastedData.length === values.length) {
         const newValues = pastedData.split('');
         setValues(newValues);
         newValues.forEach((value, index) => {
            if (inputRefs[index].current) {
               inputRefs[index].current.value = value;
            }
         });
         inputRefs[values.length - 1]?.current?.focus();
      }
   }, [values.length, inputRefs]);

   const navigate = useNavigate();

   const handleResetTimer = useCallback(async () => {
      setTime(countdownTime);
      setTimerEnded(false);
      handleResendCode();

      try {
         const corpo = { telefoneInput: cadastro.telefone, dbName };
         await api.post('/recebe-disparo-sms', corpo);
         setAlert({ show: true, message: "Código reenviado!", variant: "success" });
      } catch (error) {
         setAlert({ show: true, message: "Telefone inválido!", variant: "danger" });
      }
   }, [cadastro.telefone, countdownTime]);

   const formatTime = useCallback((time: number) => {
      const minutes = Math.floor(time / 60);
      const seconds = time % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
   }, []);

   const handleSubmit = async () => {
      const token = values.join('');
      try {
         const corpo = { token };
         const response = await api.post('/recebe-token-validacao', corpo);
         if (response.data.message === 'Token válido') {
            cadastro.telefoneValidado = true;
            cadastro.telefone = telefoneInput!;
            handleCadastroChange(cadastro);
            if (cadastro.isEmpresa && !cadastro.reaproveitamentoAso) {
               navigate(`/aso/registro/validar-info-empresa/${cadastro.idConsulta}`);
            } else if (cadastro.reaproveitamentoAso) {
               navigate('/aso/registro/validar-info-empresa');
            } else {
               if (location.pathname === `/aso/registro/disparo-sms-token-aso/${telefoneInput}/${dbName}`) {
                  navigate(`/aso/registro/validar-info-aso/${dbName}`);
               } else {
                  navigate(`/aso/registro/validar-info/${cadastro.idConsulta}/${dbName}`);
               }
            }
         } else {
            setAlert({ show: true, message: "Erro ao validar Token, por favor reenvie o código!", variant: "danger" });
         }
      } catch (error) {
         setAlert({ show: true, message: "Token inválido", variant: "danger" });
      }
   };

   return (
      <div className="enter-code">
         <div className="center-card__header"></div>
         <div className="andes-card2 center-card__body andes-card--flat andes-card--padding-16">
            <div className="enter-code-content">
               <h1 className="enter-code-content__title">Digite o código que enviamos por WhatsApp</h1>
               <div className="enter-code-content__caption">
                  <div>
                     Pronto! Foi enviado para o seu celular: {cadastro.telefone}<br />
                     Digite o token.
                  </div>
               </div>
               <form>
                  <input type="hidden" />
                  <div className="andes-form-control--numeric andes-code-input enter-code-content__input">
                     <div className="andes-form-control--numeric-wrapper">
                        {memoizedValues.map((value, index) => (
                           <div className="andes-form-control__textfield-space" key={index}>
                              <input
                                 className="andes-form-control__field"
                                 value={value}
                                 onChange={(e) => handleChange(e, index)}
                                 onKeyDown={(e) => handleChange(e, index)}
                                 onPaste={index === 0 ? handlePaste : undefined}  // Add onPaste to the first input
                                 ref={inputRefs[index]}
                                 type="text"
                                 maxLength={1}
                                 inputMode="numeric"
                                 pattern="[0-9]*"
                              />
                           </div>
                        ))}
                     </div>
                  </div>
                  <button type="submit" onClick={(e) => { e.preventDefault(); handleSubmit(); }} className="andes-button enter-code-content__submit andes-button--large andes-button--loud andes-button--full-width">
                     <span className="andes-button__content">Confirmar Código</span>
                  </button>
                  {!timerEnded ? (
                     <button type="button" onClick={(e) => { e.preventDefault(); handleResetTimer(); }} className="andes-button enter-code-content__retry andes-button--large andes-button--quiet andes-button--full-width" disabled>
                        <span className="andes-button__content">{"Reenviar Código " + formatTime(time)}</span >
                     </button>
                  ) : (
                     <button type="button" onClick={(e) => { e.preventDefault(); handleResetTimer(); }} className="andes-button enter-code-content__retry andes-button--large andes-button--quiet andes-button--full-width">
                        <span className="andes-button__content">Reenviar Código</span>
                     </button>
                  )}
               </form>
            </div>
         </div>
         <AlertMessage
            show={alert.show}
            message={alert.message}
            variant={alert.variant}
            onClose={() => setAlert({ ...alert, show: false })}
         />
      </div>
   );
};

export default AsoValidaTokenSms;